import Model, { Request } from '../model'
import Enum from '../enums'

import { plugin as $date } from '../../plugins/date'

import Customer from './customer'
import ReservationService from './reservationService'

export default class Waitlist extends Model {
  static modelName () {
    return 'waitlist'
  }

  static modelApiVersion () {
    return 1
  }

  objectID () {
    return this.idWaitlist
  }

  relations () {
    return {
      customer: { type: Customer },
      services: { type: ReservationService },
      status: { type: WaitlistStatus },
    }
  }

  static create ({ customer, services, dates } = {}) {
    const url = this.modelBaseURL()

    const data = {
      dates,
      customer: { id: customer.idCustomer },
      services: services
        .map((service) => {
          return {
            service:  service.service?.idService || null,
            employee: service.employee?.idEmployee || null,
          }
        }),
    }

    return this.requestItem(Request.jsonPost(url, data), this)
  }

  static filtered ({ page = 1, filter = null } = {}) {
    let url = this.modelBaseURL() + '/?page=' + page

    if (filter) {
      url += `&filter=${filter}`
    }

    return this.requestList(Request.get(url), Waitlist)
  }

  cancel () {
    const url = this.constructor.modelBaseURL() + '/cancel?id=' + this.objectID()
    return this.constructor.requestItem(Request.get(url), this.constructor).then(this.updateSelf(res => this.updateSelf(res)))
  }

  email () {
    const url = this.constructor.modelBaseURL() + '/email?id=' + this.objectID()
    return this.constructor.requestSuccess(Request.get(url))
  }

  notify () {
    const url = this.constructor.modelBaseURL() + '/notify?id=' + this.objectID()
    return this.constructor.requestSuccess(Request.get(url))
  }

  get servicesLabel () {
    const services = this.services

    const labels = services
      .map((service) => {
        return service.service.title
      })
      .filter((value, index, array) => {
        return array.indexOf(value) === index
      })

    return labels.join(', ')
  }

  get employeesLabel () {
    const services = this.services

    const labels = services
      .map((service) => {
        return service.employee ? service.employee.displayName : window.$nuxt.$t('service.firstAvailable')
      })
      .filter((value, index, array) => {
        return array.indexOf(value) === index
      })

    return labels.join(', ')
  }

  get datesLabel () {
    const dates = this.dates

    const labels = dates
      .map((date) => {
        let dateLabel = ''
        let timeLabel = ''

        if (date.type === 'day') {
          dateLabel += date.day
        }

        if (date.type === 'date') {
          const utc = $date.utc(new Date(date.date * 1000))
          dateLabel += $date.format(utc, $date.presets.long)
        }

        if (date.startTime || date.endTime) {
          if (date.startTime && date.endTime) {
            const startTime = $date.minutes(date.startTime)
            const endTime = $date.minutes(date.endTime)
            timeLabel = window.$nuxt.$t('waitlist.afterAndBefore', { startTime, endTime })
          } else if (date.startTime) {
            const startTime = $date.minutes(date.startTime)
            timeLabel = window.$nuxt.$t('waitlist.after', { startTime })
          } else if (date.endTime) {
            const endTime = $date.minutes(date.endTime)
            timeLabel = window.$nuxt.$t('waitlist.before', { endTime })
          }
        }

        return window.$nuxt.$t('waitlist.label', { dateLabel, timeLabel })
      })

    return labels
  }

  get statusColour () {
    switch (this.status) {
      case WaitlistStatus.waiting:
        return 'warning'
      case WaitlistStatus.booked:
        return 'success'
      case WaitlistStatus.offered:
      case WaitlistStatus.notified:
        return 'success'
      case WaitlistStatus.alerted:
        return 'warning'
      case WaitlistStatus.expired:
      case WaitlistStatus.cancelled:
        return 'danger'
      default:
        return 'info'
    }
  }

  get hasAvailability () {
    return !!this.availability && !!this.availability.firstSlot
  }

  get isCancelled () {
    return this.status === WaitlistStatus.cancelled
  }

  get isBooked () {
    return this.status === WaitlistStatus.booked
  }

  get isWaiting () {
    return this.status === WaitlistStatus.waiting
  }
}

export const WaitlistStatus = new Enum({
  waiting: { value: 0, description: 'waitlist.waiting' },
  alerted: { value: 1, description: 'waitlist.alerted' },
  notified: { value: 2, description: 'waitlist.notified' },
  offered: { value: 3, description: 'waitlist.offered' },
  booked: { value: 4, description: 'waitlist.booked' },
  expired: { value: 5, description: 'general.expired' },
  cancelled: { value: 6, description: 'general.cancelled' },
  available: { value: 7, description: 'general.available' },
})

